import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faBell,
  faClock,
  faPeopleArrows,
  faRightLong,
  faStopwatch,
  faUserGroup,
  faUserSlash,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import api from "../api/API";
import { useWorkspace } from "../context/WorkspaceContext";
import { useNavigate } from "react-router-dom";
import { getTaskPriorityLabel, TASK_PRIORITY, TASK_PRIORITY_OPTIONS } from "../utils/taskPriority";
import "./DailyGestorPage.css";

const parseCollection = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data?.status === false) {
    throw new Error(response?.data?.message || "Nao foi possivel carregar os dados.");
  }
  return response?.data?.records || response?.data?.data || [];
};

const getTaskResponsibleId = (task) =>
  String(task?.id_usuario_responsavel ?? task?.responsavel_id ?? "");

const getTaskResponsibleName = (task, members) => {
  const responsibleId = getTaskResponsibleId(task);
  if (task?.responsavel) return task.responsavel;

  const found = members.find(
    (member) => String(member?.id_usuario ?? member?.id ?? member?.user_id ?? "") === responsibleId
  );

  return found?.descricao || found?.nome || found?.email || "Sem dono";
};

const getTaskStage = (task) =>
  String(task?.id_etapa_nome || task?.etapa || task?.etapa_nome || "").trim();

const getTaskPriorityName = (task) => {
  const value = String(task?.prioridade ?? task?.prioridade_label ?? task?.prioridade_nome ?? "");
  return getTaskPriorityLabel(value);
};

const getTaskActivityDate = (task) => {
  const candidates = [
    task?.atualizado_em,
    task?.updated_at,
    task?.data_atualizacao,
    task?.modified_at,
    task?.criado_em,
    task?.created_at,
  ];

  const valid = candidates.find((value) => value && !Number.isNaN(new Date(value).getTime()));
  return valid ? new Date(valid) : null;
};

const getDueDate = (task) => {
  if (!task?.vencimento) return null;
  const date = new Date(task.vencimento);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDurationLabel = (startDate, endDate = new Date()) => {
  if (!startDate) return "Sem historico";
  const diffMs = Math.max(endDate.getTime() - startDate.getTime(), 0);
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days >= 1) {
    const remainderHours = hours % 24;
    return remainderHours > 0 ? `${days}d ${remainderHours}h` : `${days}d`;
  }

  if (hours >= 1) return `${hours}h`;

  const minutes = Math.max(Math.floor(diffMs / (1000 * 60)), 1);
  return `${minutes}min`;
};

const formatDate = (value) => {
  if (!value) return "Sem prazo";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sem prazo";
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
  });
};

const getOverdueInfo = (task) => {
  const dueDate = getDueDate(task);
  if (!dueDate) return { label: "Sem prazo", isOverdue: false };
  const now = new Date();
  if (dueDate.getTime() >= now.getTime()) {
    return { label: formatDate(dueDate), isOverdue: false };
  }
  return {
    label: `${getDurationLabel(dueDate, now)} vencida`,
    isOverdue: true,
  };
};

const getStalledInfo = (task) => {
  const activityDate = getTaskActivityDate(task);
  return {
    label: `${getDurationLabel(activityDate)} parada`,
    isStalled: activityDate ? new Date().getTime() - activityDate.getTime() >= 1000 * 60 * 60 * 24 * 2 : false,
  };
};

const DailyGestorPage = () => {
  const navigate = useNavigate();
  const { selectedWorkspaceId, selectedWorkspace, loading: loadingWorkspace, error: workspaceError } = useWorkspace();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTaskId, setSavingTaskId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [filters, setFilters] = useState({
    memberId: "all",
    projectId: "all",
    focus: "all",
  });
  const [drafts, setDrafts] = useState({});

  const loadData = async () => {
    if (!selectedWorkspaceId) {
      setTasks([]);
      setProjects([]);
      setMembers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const [tasksRes, projectsRes, membersRes] = await Promise.all([
        api.get(`/tasks?workspaceId=${selectedWorkspaceId}`),
        api.get(`/projects?workspaceId=${selectedWorkspaceId}`),
        api.get(`/workspaces/${selectedWorkspaceId}/members`),
      ]);

      setTasks(parseCollection(tasksRes));
      setProjects(parseCollection(projectsRes));
      setMembers(parseCollection(membersRes));
    } catch (error) {
      console.error("DailyGestorPage: erro ao carregar dados", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel carregar a daily do gestor."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedWorkspaceId]);

  const activeTasks = useMemo(
    () => tasks.filter((task) => !task.concluido && task.id_tarefa == null),
    [tasks]
  );

  const actionableTasks = useMemo(() => {
    return activeTasks.filter((task) => {
      const stalled = getStalledInfo(task).isStalled;
      const overdue = getOverdueInfo(task).isOverdue;
      const noOwner = !getTaskResponsibleId(task);
      const stage = getTaskStage(task).toLowerCase();
      const inBacklog = !task.id_projeto || !task.id_etapa || stage === "backlog";

      if (filters.memberId !== "all" && getTaskResponsibleId(task) !== filters.memberId) return false;
      if (filters.projectId !== "all" && String(task.id_projeto || "") !== filters.projectId) return false;

      if (filters.focus === "overdue") return overdue;
      if (filters.focus === "stalled") return stalled;
      if (filters.focus === "inbox") return inBacklog;
      if (filters.focus === "unassigned") return noOwner;

      return stalled || overdue || noOwner || inBacklog;
    });
  }, [activeTasks, filters]);

  const metrics = useMemo(() => {
    const overdue = activeTasks.filter((task) => getOverdueInfo(task).isOverdue).length;
    const stalled = activeTasks.filter((task) => getStalledInfo(task).isStalled).length;
    const unassigned = activeTasks.filter((task) => !getTaskResponsibleId(task)).length;
    const backlog = activeTasks.filter((task) => {
      const stage = getTaskStage(task).toLowerCase();
      return !task.id_projeto || !task.id_etapa || stage === "backlog";
    }).length;

    return {
      total: activeTasks.length,
      overdue,
      stalled,
      unassigned,
      backlog,
    };
  }, [activeTasks]);

  const peopleCards = useMemo(() => {
    const grouped = new Map();

    activeTasks.forEach((task) => {
      const key = getTaskResponsibleId(task) || "sem-dono";
      const name = getTaskResponsibleName(task, members);

      if (!grouped.has(key)) {
        grouped.set(key, {
          id: key,
          nome: name,
          total: 0,
          overdue: 0,
          stalled: 0,
          backlog: 0,
          tasks: [],
        });
      }

      const entry = grouped.get(key);
      entry.total += 1;
      if (getOverdueInfo(task).isOverdue) entry.overdue += 1;
      if (getStalledInfo(task).isStalled) entry.stalled += 1;

      const stage = getTaskStage(task).toLowerCase();
      if (!task.id_projeto || !task.id_etapa || stage === "backlog") entry.backlog += 1;

      entry.tasks.push(task);
    });

    return Array.from(grouped.values())
      .map((person) => ({
        ...person,
        highlights: [...person.tasks]
          .sort((left, right) => {
            const leftScore =
              (getOverdueInfo(left).isOverdue ? 3 : 0) +
              (getStalledInfo(left).isStalled ? 2 : 0) +
              (!getTaskResponsibleId(left) ? 1 : 0);
            const rightScore =
              (getOverdueInfo(right).isOverdue ? 3 : 0) +
              (getStalledInfo(right).isStalled ? 2 : 0) +
              (!getTaskResponsibleId(right) ? 1 : 0);
            return rightScore - leftScore;
          })
          .slice(0, 3),
      }))
      .sort((left, right) => {
        if (right.overdue !== left.overdue) return right.overdue - left.overdue;
        if (right.stalled !== left.stalled) return right.stalled - left.stalled;
        return right.total - left.total;
      });
  }, [activeTasks, members]);

  useEffect(() => {
    const nextDrafts = {};
    activeTasks.forEach((task) => {
      nextDrafts[task.id] = {
        id_projeto: task.id_projeto ? String(task.id_projeto) : "",
        id_usuario_responsavel: getTaskResponsibleId(task),
        prioridade: task.prioridade ? String(task.prioridade) : String(TASK_PRIORITY.MEDIUM),
        vencimento: task.vencimento ? String(task.vencimento).slice(0, 10) : "",
      };
    });
    setDrafts(nextDrafts);
  }, [activeTasks]);

  const handleDraftChange = (taskId, field, value) => {
    setDrafts((current) => ({
      ...current,
      [taskId]: {
        ...current[taskId],
        [field]: value,
      },
    }));
  };

  const handleUpdateTask = async (task) => {
    const draft = drafts[task.id] || {};

    try {
      setSavingTaskId(String(task.id));
      setErrorMessage("");
      setSuccessMessage("");

      await api.put(`/tasks/${task.id}`, {
        fields: {
          id_workspace: selectedWorkspaceId,
          id_projeto: draft.id_projeto ? Number(draft.id_projeto) : null,
          id_usuario_responsavel: draft.id_usuario_responsavel ? Number(draft.id_usuario_responsavel) : null,
          prioridade: draft.prioridade ? Number(draft.prioridade) : null,
          vencimento: draft.vencimento || null,
        },
      });

      setSuccessMessage("Tarefa redistribuida com sucesso.");
      loadData();
    } catch (error) {
      console.error("DailyGestorPage: erro ao redistribuir tarefa", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel atualizar a tarefa."
      );
    } finally {
      setSavingTaskId("");
    }
  };

  return (
    <Container fluid className="daily-gestor-page">
      <section className="daily-gestor-hero">
        <div>
          <p className="daily-gestor-kicker">Daily command center</p>
          <h1>Daily do Gestor</h1>
          <p className="daily-gestor-subtitle">
            Entre na daily vendo exatamente onde o fluxo travou, quem esta sobrecarregado e o que precisa ser redistribuido agora.
          </p>
        </div>

        <div className="daily-gestor-hero-side">
          <div className="daily-gestor-pill">
            <span>Workspace ativo</span>
            <strong>{selectedWorkspace?.nome || "Nenhum selecionado"}</strong>
          </div>
          <div className="daily-gestor-note">
            Use esta visao para sair da reuniÃ£o com dono, prazo e prioridade definidos para cada pendencia critica.
          </div>
        </div>
      </section>

      {workspaceError && <Alert variant="warning">{workspaceError}</Alert>}
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      {!selectedWorkspaceId && !loadingWorkspace ? (
        <Alert variant="info">Selecione um workspace no topo para abrir a daily do time.</Alert>
      ) : loading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" variant="dark" />
        </div>
      ) : (
        <>
          <Row className="g-3 daily-gestor-metrics">
            <Col md={6} xl={2}>
              <Card className="daily-gestor-metric-card">
                <Card.Body>
                  <span>Ativas</span>
                  <strong>{metrics.total}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="daily-gestor-metric-card danger">
                <Card.Body>
                  <span>Vencidas</span>
                  <strong>{metrics.overdue}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="daily-gestor-metric-card warning">
                <Card.Body>
                  <span>Paradas</span>
                  <strong>{metrics.stalled}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="daily-gestor-metric-card neutral">
                <Card.Body>
                  <span>Sem dono</span>
                  <strong>{metrics.unassigned}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="daily-gestor-metric-card insight">
                <Card.Body>
                  <span>Backlog / Inbox</span>
                  <strong>{metrics.backlog}</strong>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={2}>
              <Card className="daily-gestor-metric-card accent">
                <Card.Body>
                  <span>Pessoas em foco</span>
                  <strong>{peopleCards.length}</strong>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-4">
            <Col xl={8}>
              <Card className="daily-gestor-panel">
                <Card.Body>
                  <div className="daily-gestor-panel-header">
                    <div>
                      <h4>
                        <FontAwesomeIcon icon={faPeopleArrows} /> Mesa de distribuicao
                      </h4>
                      <p>As tarefas que precisam de uma decisao durante a daily: backlog, sem dono, vencidas ou paradas.</p>
                    </div>
                    <Badge bg="dark">{actionableTasks.length} em revisao</Badge>
                  </div>

                  <div className="daily-gestor-filters">
                    <Form.Select
                      value={filters.focus}
                      onChange={(event) => setFilters((current) => ({ ...current, focus: event.target.value }))}
                    >
                      <option value="all">Tudo que exige decisao</option>
                      <option value="overdue">So vencidas</option>
                      <option value="stalled">So paradas</option>
                      <option value="unassigned">So sem dono</option>
                      <option value="inbox">So backlog / inbox</option>
                    </Form.Select>

                    <Form.Select
                      value={filters.projectId}
                      onChange={(event) => setFilters((current) => ({ ...current, projectId: event.target.value }))}
                    >
                      <option value="all">Todos os projetos</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.descricao}
                        </option>
                      ))}
                    </Form.Select>

                    <Form.Select
                      value={filters.memberId}
                      onChange={(event) => setFilters((current) => ({ ...current, memberId: event.target.value }))}
                    >
                      <option value="all">Todo o time</option>
                      {members.map((member) => (
                        <option key={member.id_usuario ?? member.id} value={String(member.id_usuario ?? member.id)}>
                          {member.descricao || member.nome || member.email}
                        </option>
                      ))}
                    </Form.Select>
                  </div>

                  <div className="daily-gestor-task-list">
                    {actionableTasks.length === 0 ? (
                      <div className="daily-gestor-empty-state">
                        Nenhuma tarefa critica para a daily neste momento.
                      </div>
                    ) : (
                      actionableTasks.map((task) => {
                        const draft = drafts[task.id] || {};
                        const overdueInfo = getOverdueInfo(task);
                        const stalledInfo = getStalledInfo(task);

                        return (
                          <article key={task.id} className="daily-gestor-task-card">
                            <div className="daily-gestor-task-copy">
                              <div className="daily-gestor-task-top">
                                <div>
                                  <h5>{task.descricao}</h5>
                                  <p>{task.detalhe || "Sem detalhe adicional registrado."}</p>
                                </div>
                                <div className="daily-gestor-task-badges">
                                  <Badge bg={overdueInfo.isOverdue ? "danger" : "secondary"}>{overdueInfo.label}</Badge>
                                  <Badge bg={stalledInfo.isStalled ? "warning" : "light"} text="dark">
                                    {stalledInfo.label}
                                  </Badge>
                                  <Badge bg="light" text="dark">{getTaskPriorityName(task)}</Badge>
                                </div>
                              </div>

                              <div className="daily-gestor-task-meta">
                                <span><FontAwesomeIcon icon={faBell} /> {task.projeto || "Sem projeto"}</span>
                                <span><FontAwesomeIcon icon={faUserSlash} /> {getTaskResponsibleName(task, members)}</span>
                                <span><FontAwesomeIcon icon={faClock} /> {getTaskStage(task) || "Backlog"}</span>
                                <span><FontAwesomeIcon icon={faStopwatch} /> Criada em {formatDate(task.criado_em || task.created_at)}</span>
                              </div>
                            </div>

                            <div className="daily-gestor-task-form">
                              <Form.Select
                                value={draft.id_projeto || ""}
                                onChange={(event) => handleDraftChange(task.id, "id_projeto", event.target.value)}
                              >
                                <option value="">Escolha o projeto</option>
                                {projects.map((project) => (
                                  <option key={project.id} value={project.id}>
                                    {project.descricao}
                                  </option>
                                ))}
                              </Form.Select>

                              <Form.Select
                                value={draft.id_usuario_responsavel || ""}
                                onChange={(event) => handleDraftChange(task.id, "id_usuario_responsavel", event.target.value)}
                              >
                                <option value="">Defina o responsavel</option>
                                {members.map((member) => (
                                  <option key={member.id_usuario ?? member.id} value={String(member.id_usuario ?? member.id)}>
                                    {member.descricao || member.nome || member.email}
                                  </option>
                                ))}
                              </Form.Select>

                              <Form.Select
                                value={draft.prioridade || String(TASK_PRIORITY.MEDIUM)}
                                onChange={(event) => handleDraftChange(task.id, "prioridade", event.target.value)}
                              >
                                {TASK_PRIORITY_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </Form.Select>

                              <Form.Control
                                type="date"
                                value={draft.vencimento || ""}
                                onChange={(event) => handleDraftChange(task.id, "vencimento", event.target.value)}
                              />

                              <Button
                                variant="dark"
                                onClick={() => handleUpdateTask(task)}
                                disabled={savingTaskId === String(task.id)}
                              >
                                <FontAwesomeIcon icon={faWandMagicSparkles} />{" "}
                                {savingTaskId === String(task.id) ? "Salvando..." : "Aplicar"}
                              </Button>
                            </div>
                          </article>
                        );
                      })
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={4}>
              <Card className="daily-gestor-panel">
                <Card.Body>
                  <div className="daily-gestor-panel-header">
                    <div>
                      <h4>
                        <FontAwesomeIcon icon={faUserGroup} /> Leitura por atendente
                      </h4>
                      <p>Veja carga, atraso e tarefas mais sensiveis por pessoa antes de distribuir mais demanda.</p>
                    </div>
                  </div>

                  <div className="daily-gestor-people-list">
                    {peopleCards.length === 0 ? (
                      <div className="daily-gestor-empty-state">Sem tarefas ativas para o time no momento.</div>
                    ) : (
                      peopleCards.map((person) => (
                        <article key={person.id} className="daily-gestor-person-card">
                          <div className="daily-gestor-person-header">
                            <div>
                              <strong>{person.nome}</strong>
                              <small>{person.total} tarefa(s) em andamento</small>
                            </div>
                            <Badge bg={person.overdue > 0 ? "danger" : "dark"}>
                              {person.overdue} vencida(s)
                            </Badge>
                          </div>

                          <div className="daily-gestor-person-stats">
                            <span><FontAwesomeIcon icon={faArrowTrendUp} /> {person.stalled} parada(s)</span>
                            <span><FontAwesomeIcon icon={faBell} /> {person.backlog} no backlog</span>
                          </div>

                          <div className="daily-gestor-person-highlights">
                            {person.highlights.map((task) => (
                              <div key={task.id} className="daily-gestor-highlight-item">
                                <strong>{task.descricao}</strong>
                                <small>
                                  {getOverdueInfo(task).label} • {getStalledInfo(task).label}
                                </small>
                              </div>
                            ))}
                          </div>
                        </article>
                      ))
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <section className="daily-gestor-members-section">
            <Card className="daily-gestor-panel">
              <Card.Body>
                <div className="daily-gestor-panel-header">
                  <div>
                    <h4>
                      <FontAwesomeIcon icon={faUserGroup} /> Convites e time do workspace
                    </h4>
                    <p>Na daily, use este atalho quando perceber que falta alguem no fluxo. A gestao completa fica na tela de perfil.</p>
                  </div>
                  <Badge bg="light" text="dark">{members.length} membro(s)</Badge>
                </div>

                <div className="daily-gestor-invite-callout">
                  <div>
                    <strong>{selectedWorkspace?.nome || "Workspace"} pronto para crescer</strong>
                    <p className="mb-0">
                      Convide novas pessoas, altere papeis e mantenha a configuracao do time centralizada na area de perfil.
                    </p>
                  </div>

                  <div className="daily-gestor-invite-stats">
                    <span>Time atual</span>
                    <strong>{members.length}</strong>
                  </div>

                  <Button variant="dark" onClick={() => navigate("/perfil")}>
                    Gerir membros no perfil <FontAwesomeIcon icon={faRightLong} />
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </section>
        </>
      )}
    </Container>
  );
};

export default DailyGestorPage;
